/**
 * Stale-bundle detection — handles the «session opened before a deploy»
 * race that surfaces as a cryptic `SyntaxError: Unexpected token '<'`
 * from a dynamic `import()`.
 *
 * Background: Vercel SPA-rewrite catches every unmatched request and
 * returns `index.html`. After a deploy, the chunk filename hashes change.
 * A tab opened with the previous bundle still references the OLD hash;
 * when its lazy `import('agora-rtc-sdk-ng')` (or any other dynamic
 * import) fires, the CDN returns HTML — the JS parser swallows the
 * first `<` of `<!DOCTYPE html>` and throws SyntaxError. The master
 * sees the catch-block's local error text («Не удалось …»), which
 * reads like an Agora-side problem.
 *
 * This module:
 *  - `isChunkLoadError` — pure check, recognises the SyntaxError /
 *    ChunkLoadError signatures most bundlers + browsers produce.
 *  - `triggerStaleBundle()` — fires a global event so the in-tree
 *    banner (StaleBundleBanner.tsx) can render a one-tap «Обновить»
 *    CTA. Idempotent within a session.
 *  - `STALE_BUNDLE_EVENT` — DeviceEventEmitter key used by both sides.
 *
 * Diagnosed off RtcTelemetryEvents for LiveId=1737 (2026-05-27): one
 * user, one chunk hash, fired ~49 min after the prod deploy.
 */
import { DeviceEventEmitter, Platform } from 'react-native';

import { analytics } from '../services/analytics';

export const STALE_BUNDLE_EVENT = 'agape:stale-bundle';

/** True if `err` looks like a chunk-load failure caused by a stale tab
 *  pointing at a chunk hash that no longer exists on the CDN. */
export function isChunkLoadError(err: unknown): boolean {
    if (!err) return false;
    const anyErr = err as { name?: string; message?: string };
    const name = anyErr.name ?? '';
    const msg = anyErr.message ?? '';

    // Webpack / Vite / Next chunk-load convention.
    if (name === 'ChunkLoadError') return true;
    if (/loading chunk \S+ failed/i.test(msg)) return true;
    if (/loading css chunk \S+ failed/i.test(msg)) return true;

    // The Vercel-SPA-fallback signature. The CDN returns `<!DOCTYPE html>`
    // when the chunk URL no longer exists; the JS parser explodes on the
    // first `<`. Exact wording differs by engine — V8 says «Unexpected
    // token '<'», SpiderMonkey says «expected expression, got '<'»,
    // WebKit says «Unexpected token '<<'» in some versions.
    if (name === 'SyntaxError' && /[uU]nexpected token.*</.test(msg)) return true;
    if (/expected expression.*</.test(msg)) return true;

    // Generic «failed to fetch dynamic-imported module» — emitted by some
    // engines when the network layer reports HTML but parsing never even
    // starts. Treat the same way; refresh almost always resolves it.
    if (/failed to fetch dynamically imported module/i.test(msg)) return true;

    // CORE-714 — Metro's own async-require path, which every lazily loaded
    // route goes through. None of the SyntaxError signatures above match it:
    // on the RU edge nginx returns an honest 404 for the missing chunk, so
    // parsing never starts and Metro rejects with its own error instead:
    //   «Loading module https://agapella.io/_expo/static/js/web/
    //    LiveDetailScreen-<hash>.js failed.»
    // Observed verbatim in Sentry 2026-08-01 for a user on МГТС whose tab
    // predated the deploy. This is the signature that actually reaches users
    // — the Vercel one only fires where the SPA fallback masks the 404.
    if (name === 'AsyncRequireError') return true;
    if (/loading module \S+ failed/i.test(msg)) return true;

    // Safari / Firefox wording for a dynamic import whose URL 404s.
    if (/importing a module script failed/i.test(msg)) return true;
    if (/error loading dynamically imported module/i.test(msg)) return true;

    return false;
}

/** Guard against double-firing — once the banner is up, further chunk
 *  errors in the same session are noise. */
let triggered = false;

/** How many live sessions are on screen right now (viewer + publisher can
 *  overlap). Above zero the banner is held back — see setLiveSessionActive. */
let liveSessionDepth = 0;

/** The reason we held back, replayed when the last эфир ends. */
let deferredReason: string | null = null;

/** Show the stale-bundle banner. Safe to call from any catch block.
 *  No-op on native (the chunk-on-CDN race is web-only).
 *
 *  `reason` names what noticed the staleness — `safe-import`, `global-error`,
 *  `unhandled-rejection`, `asset-error` or `version-poll`. It rides along to
 *  analytics so we can tell whether the prompt fires because something already
 *  broke or because the poll caught the deploy first (the latter is the win). */
export function triggerStaleBundle(reason: string = 'safe-import'): void {
    if (triggered) return;
    if (Platform.OS !== 'web') return;
    // Never over an эфир. The banner is a full-screen blocking modal and the
    // CTA reloads the page — either one ends a broadcast the master can't get
    // back. Hold it and raise it once they're off air.
    if (liveSessionDepth > 0) {
        deferredReason = deferredReason ?? reason;
        return;
    }
    emitStaleBundle(reason, false);
}

function emitStaleBundle(reason: string, deferred: boolean): void {
    if (triggered) return;
    triggered = true;
    try {
        analytics.track('stale_bundle_prompt', { reason, deferred });
    } catch {
        // Analytics must never stand between the user and the reload CTA.
    }
    try {
        DeviceEventEmitter.emit(STALE_BUNDLE_EVENT);
    } catch {
        // DeviceEventEmitter shouldn't throw, but if it does we'd rather
        // let the original SyntaxError bubble than crash the catch chain.
    }
}

/**
 * Mark a live session as running (`true` on entering the эфир, `false` on
 * leaving). Refcounted, because a master can be publishing while the viewer
 * component for the same live is also mounted — the banner stays held until
 * the LAST one releases.
 *
 * While held, a detected stale bundle is remembered, not shown; the moment the
 * эфир ends the banner appears with the original reason. Nothing is lost, the
 * broadcast just isn't interrupted to deliver it.
 */
/** CORE-1884 — a newer deploy is known (shown or held for after the эфир). */
export function isStaleBundleKnown(): boolean {
    return triggered || deferredReason !== null;
}

export function setLiveSessionActive(active: boolean): void {
    if (Platform.OS !== 'web') return;
    liveSessionDepth = Math.max(0, liveSessionDepth + (active ? 1 : -1));
    if (liveSessionDepth === 0 && deferredReason !== null) {
        const reason = deferredReason;
        deferredReason = null;
        emitStaleBundle(reason, true);
    }
}

/** Test-only — reset the one-shot guard. Do not call from production code. */
export function _resetStaleBundleForTest(): void {
    triggered = false;
    installed = false;
    lastVersionCheckAt = 0;
    runningEntryChunk = null;
    liveSessionDepth = 0;
    deferredReason = null;
}

// ── Global guard (CORE-714) ──────────────────────────────────────────────
//
// `safeImport` only covers the dynamic imports we wrap by hand (Agora, social
// auth, device enumeration). The chunk failure users actually hit comes from
// Metro's async-require for lazily loaded ROUTES, which we never wrap — it
// surfaces as an unhandled rejection and the banner stayed silent. The
// listeners below are that missing net.

let installed = false;
let runningEntryChunk: string | null = null;
let lastVersionCheckAt = 0;

/** Poll the shell at most this often — a deploy the user hasn't tripped over
 *  yet is not urgent, and we don't want a request per tab-focus. */
const VERSION_CHECK_MIN_INTERVAL_MS = 15 * 60 * 1000;

const ENTRY_CHUNK_RE = /\/_expo\/static\/js\/web\/(index-[a-f0-9]+\.js)/;

/** Filename of the entry bundle this tab is running, read off the DOM at
 *  install time. `null` on the Expo dev server (unhashed bundle) — the version
 *  poll then stays off, which is what we want locally. */
function readRunningEntryChunk(): string | null {
    try {
        const scripts = Array.from(document.querySelectorAll('script[src]'));
        for (const s of scripts) {
            const match = ENTRY_CHUNK_RE.exec((s as HTMLScriptElement).src || '');
            if (match) return match[1];
        }
    } catch {
        // Defensive: a document without querySelectorAll isn't a browser we
        // need to serve.
    }
    return null;
}

/**
 * Ask the origin which entry bundle it serves NOW and compare with ours.
 * Catches the «tab left open across a deploy» case BEFORE the user navigates
 * into a route whose chunk is already gone.
 *
 * The fetch is same-origin on every host we serve (CORE-940 — the apex stopped
 * 302'ing to app.agapella.io), so this now runs for apex visitors too. Anything
 * that does throw — offline, CORS on some future origin — stays a silent no-op;
 * those users are covered by the error listeners instead.
 */
async function checkDeployedShell(): Promise<void> {
    if (!runningEntryChunk) return;
    const now = Date.now();
    if (now - lastVersionCheckAt < VERSION_CHECK_MIN_INTERVAL_MS) return;
    lastVersionCheckAt = now;

    try {
        const res = await fetch(`${window.location.origin}/`, {
            cache: 'no-store',
            credentials: 'omit',
        });
        if (!res.ok) return;
        const match = ENTRY_CHUNK_RE.exec(await res.text());
        if (match && match[1] !== runningEntryChunk) triggerStaleBundle('version-poll');
    } catch {
        // Offline, CORS, or the edge is having a moment — none of that is
        // evidence of a stale bundle, so stay quiet.
    }
}

/**
 * Install the global chunk-failure listeners. Call once at app start (web
 * only — native has no CDN chunk race). Idempotent.
 *
 * We deliberately do NOT auto-reload: the banner's one-tap «Обновить» is the
 * whole UX. A silent `location.reload()` would tear down an Agora session
 * mid-эфир, which is a far worse failure than the broken screen it fixes.
 */
export function installStaleBundleGuard(): void {
    if (installed) return;
    if (Platform.OS !== 'web') return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    installed = true;

    // Metro's async-require rejects here — the LiveDetailScreen case.
    window.addEventListener('unhandledrejection', (event) => {
        if (isChunkLoadError((event as PromiseRejectionEvent).reason)) {
            triggerStaleBundle('unhandled-rejection');
        }
    });

    // Capture phase: `error` on a <script> that 404s does not bubble, so a
    // listener on window only sees it while capturing.
    window.addEventListener(
        'error',
        (event) => {
            const target = event.target as HTMLScriptElement | null;
            const src = target && target.tagName === 'SCRIPT' ? target.src || '' : '';
            if (src.includes('/_expo/static/js/web/')) {
                triggerStaleBundle('asset-error');
                return;
            }
            if (isChunkLoadError((event as ErrorEvent).error)) {
                triggerStaleBundle('global-error');
            }
        },
        true,
    );

    runningEntryChunk = readRunningEntryChunk();

    // Check the deployed shell immediately. Previously we waited until the
    // tab changed visibility, so a restored/cached home could sit on screen
    // for minutes before we noticed that its entry bundle was obsolete.
    void checkDeployedShell();

    // A page restored from the browser back/forward cache is visually complete
    // before React effects run again — exactly the situation where an old home
    // can briefly look current. Bypass the normal 15-minute throttle for that
    // one restore and compare against the live shell straight away.
    window.addEventListener('pageshow', (event) => {
        if (!(event as PageTransitionEvent).persisted) return;
        lastVersionCheckAt = 0;
        void checkDeployedShell();
    });

    // Re-check when the user comes back to the tab — that's exactly when a
    // long-idle session is most likely to have been left behind by a deploy.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void checkDeployedShell();
    });
}

/**
 * Wrap a dynamic-import lambda so any chunk-load error trips the banner
 * before re-throwing. Use this for any `() => import('…')` we control:
 *
 * ```ts
 * const AgoraRTC = (await safeImport(() => import('agora-rtc-sdk-ng'))).default;
 * ```
 *
 * Non-chunk-load failures bubble unchanged so existing catch blocks keep
 * their own error-handling semantics (mic-denied alerts, etc.).
 */
export async function safeImport<T>(loader: () => Promise<T>): Promise<T> {
    try {
        return await loader();
    } catch (err) {
        if (isChunkLoadError(err)) triggerStaleBundle();
        throw err;
    }
}

/**
 * CORE-1266 — retry a dynamic import before giving up on it.
 *
 * `React.lazy(() => import('…'))` throws into the nearest error boundary the
 * moment the chunk fetch fails. On the master dashboard the only lazy chunk is
 * «Путь света»; a single transient failure to fetch it (a network blip, a CDN
 * edge that 404s the chunk for one request right after a deploy) tripped the
 * root ErrorBoundary and painted the full-screen «Что-то пошло не так» — the
 * intermittent «через раз, лечится обновлением» half of the bug.
 *
 * Most of those failures clear on a second attempt, so retry the load a couple
 * of times with a short backoff before surfacing the stale-bundle banner and
 * re-throwing. Non-chunk errors bubble immediately — a retry can't help them
 * and would only delay a real failure. `delayMs` is a parameter so tests can
 * run without real timers.
 */
export async function importWithRetry<T>(
    loader: () => Promise<T>,
    retries = 2,
    delayMs = 400,
): Promise<T> {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            return await loader();
        } catch (err) {
            lastErr = err;
            // A non-chunk error won't heal on retry; fail fast.
            if (!isChunkLoadError(err) || attempt === retries) break;
            if (delayMs > 0) {
                await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
            }
        }
    }
    if (isChunkLoadError(lastErr)) triggerStaleBundle('lazy-retry');
    throw lastErr;
}
